import { IS_AUTOCOMPLETE_ENABLED } from '@/constants';
import type { Address } from '@/modules/address/domain/Address';
import { isDeviceKiosco } from '@/modules/perseo-billing/utils';
import { extractPhonePrefix } from '@/modules/shared/utils/phonePrefix';
import { FORM_DEFAULT_VAUES } from '@/sections/address/components/AddressFormModal/constants';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';

const KIOSCO_ALIAS = 'Kiosco';
const KIOSCO_STREET_NUMBER = '0';

const getAddressToEdit = (address: Address, isKiosco: boolean) => {
  if (isKiosco) {
    return {
      ...address,
      alias: KIOSCO_ALIAS,
      streetNumber: KIOSCO_STREET_NUMBER,
      region: +address?.region
    };
  }

  return {
    ...address,
    region: +address?.region
  };
};

const shouldEnableAutocomplete = (
  address: Address,
  isGuest: boolean
): boolean => {
  if (IS_AUTOCOMPLETE_ENABLED === false) return false;

  const hasAddress = !!address;
  const isNewAddress = !address?.id && !address?.streetName;
  if (isNewAddress && isGuest) return true;

  const streetName = address?.streetName;
  const hasShippingAddress = !!streetName;
  const hasIncompleteAddress = hasAddress && !hasShippingAddress;

  const latitude = address?.lat ?? address?.latitude;
  const longitude = address?.lon ?? address?.longitude;
  const hasLatAndLon = !!latitude && !!longitude;

  const isAutocompleteEnabled = hasLatAndLon || hasIncompleteAddress;
  return isAutocompleteEnabled;
};

const shouldEnableNoNumberCheckbox = (address: Address): boolean => {
  const hasAddress = !!address;
  const streetName = address?.streetName;
  const streetNumber = address?.streetNumber;

  const hasShippingAddress = !!streetName;
  const hasIncompleteAddress = hasAddress && !hasShippingAddress;

  const hasStreetNumber = streetNumber && streetNumber !== '';
  const noNumber = hasIncompleteAddress ? false : !hasStreetNumber;
  return noNumber;
};

export const getFormDefaultValues = (
  isGuest: boolean,
  address?: Address
): AddressFormValues => {
  if (!address) return FORM_DEFAULT_VAUES;

  const isKiosco = isDeviceKiosco();
  const addressToEdit = getAddressToEdit(address, isKiosco);

  const fullPhone = addressToEdit?.phone ?? '';
  const { prefix, number } = extractPhonePrefix(fullPhone);

  const latitude = addressToEdit?.lat ?? addressToEdit?.latitude;
  const longitude = addressToEdit?.lon ?? addressToEdit?.longitude;

  const isAutocomplete = shouldEnableAutocomplete(address, isGuest);
  const noNumber = shouldEnableNoNumberCheckbox(address);

  return {
    ...addressToEdit,
    isAutocomplete: isAutocomplete,
    phone: number.trim(),
    'no-number': noNumber,
    'prefix-phone': prefix,
    'save-address': true,
    _skipResetFields: false,
    lat: latitude,
    lon: longitude,
    latitude: latitude,
    longitude: longitude
  };
};
